
import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { EarningsChart, OrderStatusChart } from '../charts'
import { BuyerAccountsTable } from './BuyerAccountsTable'
import { formatCurrency } from '../../utils/format'
import { 
  Package, 
  DollarSign, 
  Gift, 
  Star, 
  TrendingUp, 
  RefreshCw, 
  ExternalLink,
  Eye,
  CheckCircle,
  Info,
  AlertCircle
} from 'lucide-react'

interface DashboardOverviewProps {
  stats: {
    totalOrders: number;
    deliveredOrders: number;
    pendingOrders: number;
    totalEarnings: number;
    totalOrderAmount: number;
    bonusLevel: number;
    cancelledOrders?: number;
  };
  chartType: 'line' | 'bar' | 'area';
  onChartTypeChange: (type: 'line' | 'bar' | 'area') => void;
  selectedDateRange: string;
  onChartUpdate: () => void;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  onPresetDateRangeChange?: (range: string) => void;
  isUpdating: boolean;
  onUpdateOrders: () => void;
  onViewAllOrders: () => void;
  chartData: Array<{
    name: string;
    siparisler: number;
    kazanc: number;
    bonus: number;
  }>;
  pieData: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  recentOrders: Array<{
    id: string;
    product: string;
    image?: string | null;
    date: string;
    status: string;
    bonus: number;
    amount: number;
  }>;
  lastOrderUpdateAt?: string | null;  // ✅ YENİ
}

export default function DashboardOverview({
  stats,
  chartType,
  onChartTypeChange,
  selectedDateRange,
  onChartUpdate,
  onDateRangeChange,
  onPresetDateRangeChange,
  isUpdating,
  onUpdateOrders,
  onViewAllOrders,
  chartData,
  pieData,
  recentOrders,
  lastOrderUpdateAt  // ✅ YENİ
}: DashboardOverviewProps) {

  // ✅ YENİ: Son güncelleme tarihi hesaplamaları
  const STALE_THRESHOLD_DAYS = 7;

  const lastUpdateInfo = React.useMemo(() => {
    if (!lastOrderUpdateAt) {
      return {
        relativeText: 'Henüz güncellenmedi',
        isStale: false,
        isNever: true
      };
    }

    // Backend UTC olarak gönderiyor; "Z" yoksa ekle ki JS doğru parse etsin
    const utcStr = lastOrderUpdateAt.endsWith('Z') || lastOrderUpdateAt.includes('+')
      ? lastOrderUpdateAt
      : lastOrderUpdateAt + 'Z';
    const updateDate = new Date(utcStr);
    const now = new Date();
    const diffMs = now.getTime() - updateDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let relativeText: string;
    if (diffMinutes < 1) {
      relativeText = 'Az önce';
    } else if (diffMinutes < 60) {
      relativeText = `${diffMinutes} dakika önce`;
    } else if (diffHours < 24) {
      relativeText = `${diffHours} saat önce`;
    } else if (diffDays === 1) {
      relativeText = 'Dün';
    } else if (diffDays < 30) {
      relativeText = `${diffDays} gün önce`;
    } else {
      const diffMonths = Math.floor(diffDays / 30);
      relativeText = `${diffMonths} ay önce`;
    }

    return {
      relativeText,
      isStale: diffDays >= STALE_THRESHOLD_DAYS,
      isNever: false
    };
  }, [lastOrderUpdateAt]);

  const handleUpdateClick = React.useCallback(() => {
    if (isUpdating) return
    
    window.open("https://www.amazon.com/?EBH=true", "_blank");
    
    onUpdateOrders()
  }, [isUpdating, onUpdateOrders])

  return (
    <>
{/* Modern Info Banner - Akıllı renk değişimi */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className={`relative overflow-hidden rounded-xl border shadow-lg transition-all duration-300 ${
          lastUpdateInfo.isStale
            ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50'
            : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'
        }`}>
          {/* Sol kenar renkli çizgi */}
          <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${
            lastUpdateInfo.isStale
              ? 'from-amber-400 to-yellow-500'
              : 'from-green-400 to-emerald-500'
          }`}></div>
          
          <div className="flex items-center gap-4 p-5 pl-8">
            {/* İkon */}
            <div className="flex-shrink-0">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                lastUpdateInfo.isStale ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                {lastUpdateInfo.isStale ? (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                ) : (
                  <Info className="w-6 h-6 text-green-600" />
                )}
              </div>
            </div>
            
            {/* Metin */}
            <div className="flex-1 min-w-0">
              <p className={`text-base font-medium leading-relaxed ${
                lastUpdateInfo.isStale ? 'text-amber-900' : 'text-gray-800'
              }`}>
                {lastUpdateInfo.isStale
                  ? 'Siparişlerinizi uzun zamandır güncellemediniz! Kazancınızın güncel kalması için lütfen güncelleyin.'
                  : 'Sipariş durumları manuel olarak güncellenmektedir. Kazancınızın güncel kalması için siparişlerinizi düzenli olarak güncelleyin.'
                }
              </p>
              <p className={`text-xs mt-1 flex items-center gap-1.5 ${
                lastUpdateInfo.isStale ? 'text-amber-700' : 'text-green-700'
              }`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                Son güncelleme: {lastUpdateInfo.relativeText}
              </p>
            </div>
            
            {/* Buton */}
            <div className="flex-shrink-0">
              <Button 
                className={`text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 px-6 py-2.5 rounded-lg ${
                  lastUpdateInfo.isStale
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                }`}
                onClick={handleUpdateClick}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Şimdi Güncelle
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

{/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="neon-glow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  SİPARİŞ DURUMU
                </CardTitle>
                <Package className="w-4 h-4 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent>
              {/* Toplam Sipariş */}
              <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">Toplam Sipariş</div>
                <div className="text-3xl font-bold text-foreground">
                  {stats.totalOrders || 0} Adet
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>

              {/* Teslim Edildi */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">Teslim Edildi</span>
                </div>
                <span className="text-lg font-bold text-green-600">
                  {stats.deliveredOrders || 0}
                </span>
              </div>

      {/* İlerleme Çubuğu - DÜZELTİLMİŞ */}
      <div className="mt-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
            style={{ 
              width: `${(() => {
                // İptal/İade edilmemiş (aktif) siparişleri hesapla
                const activeOrders = stats.totalOrders - (stats.cancelledOrders || 0);
                // Aktif siparişler 0 ise progress 0%
                if (activeOrders === 0) return 0;
                // Progress = Teslim Edilenler / Aktif Siparişler * 100
                return Math.round((stats.deliveredOrders / activeOrders) * 100);
              })()}%` 
            }}
          ></div>
        </div>
        <div className="text-xs text-gray-500 mt-1 text-right">
          %{(() => {
            const activeOrders = stats.totalOrders - (stats.cancelledOrders || 0);
            if (activeOrders === 0) return 0;
            return Math.round((stats.deliveredOrders / activeOrders) * 100);
          })()} Tamamlandı
        </div>
      </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="neon-glow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  TOPLAM SİPARİŞ MALİYETİ
                </CardTitle>
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground mb-2">{formatCurrency(stats.totalOrderAmount || 0)}</div>
             
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="neon-glow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  TOPLAM KAZANÇ
                </CardTitle>
                <Gift className="w-4 h-4 text-orange-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground mb-2">{formatCurrency(stats.totalEarnings)}</div>
              
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Earnings Chart */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
          <EarningsChart
            chartType={chartType}
            onChartTypeChange={onChartTypeChange}
            onRefresh={onChartUpdate}
            selectedDateRange={selectedDateRange}
            onDateRangeChange={onDateRangeChange}
            onPresetDateRangeChange={onPresetDateRangeChange}
            data={chartData}
          />
        </motion.div>

        {/* Order Status Chart */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
          <OrderStatusChart data={pieData} />
        </motion.div>
      </div>

      {/* Alıcı hesaplarına göre özet — yalnız hesap verisi geldiyse görünür (kendi içinde gizlenir) */}
      <BuyerAccountsTable />

      {/* Recent Orders */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
        <Card className="glass border-orange-200 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Son Siparişler
                </CardTitle>
                <CardDescription className="text-gray-600">
                  En son Amazon siparişleriniz
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-orange-200 hover:bg-orange-50"
                onClick={onViewAllOrders}
              >
                <Eye className="w-4 h-4 mr-2" />
                Tümünü Gör
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentOrders.map((order, index) => {
                const isCancelled = order.status === 'İptal Edildi' || 
                                   order.status === 'Para İadesi Yapıldı' ||
                                   order.status === 'İade Talebi Mevcut';
                const isDelivered = order.status === 'Teslim Edildi';
                const isPending = order.status === 'Beklemede' || order.status === 'Bekliyor';
                const isInTransit = order.status === 'Kargoda' || order.status.includes('Arriving');
                
                let statusClass = 'bg-gray-100 text-gray-700 border-gray-200';
                if (isDelivered) statusClass = 'bg-green-100 text-green-700 border-green-200';
                else if (isCancelled) statusClass = 'bg-red-100 text-red-700 border-red-200';
                else if (isInTransit) statusClass = 'bg-blue-100 text-blue-700 border-blue-200';
                
                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="flex items-center justify-between p-4 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {order.image ? (
                          <img
                            src={order.image}
                            alt=""
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const el = e.currentTarget
                              el.style.display = 'none'
                              const fb = el.nextElementSibling as HTMLElement | null
                              if (fb) fb.style.display = ''
                            }}
                          />
                        ) : null}
                        <Package
                          className="w-6 h-6 text-orange-600"
                          style={{ display: order.image ? 'none' : undefined }}
                        />
                      </div>
                      <div className="min-w-0">
                        <h4
                          className="font-medium text-gray-800 max-w-2xl break-words"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          title={order.product}
                        >
                          {order.product}
                        </h4>
                        <p className="text-sm text-gray-600">Sipariş: {order.id}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusClass} mb-1`}>
                        {order.status}
                      </div>
                   {isCancelled ? (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold bg-red-50 text-red-600 border border-red-200 line-through decoration-2">
    +{formatCurrency(order.bonus)}
  </span>
) : isDelivered ? (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold bg-green-50 text-green-600 border border-green-200">
    +{formatCurrency(order.bonus)}
  </span>
) : isInTransit ? (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-200">
    +{formatCurrency(order.bonus)}
  </span>
) : (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold bg-gray-50 text-gray-600 border border-gray-200">
    +{formatCurrency(order.bonus)}
  </span>
)}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </>
  )
}